import {useCallback, useEffect} from 'react';
import {UpdateEmailSubscriptionsResponse} from '../../../grpc_client/grips/protos/email_subscriptions/v1/email_subscriptions_service';
import {useUserSessionContext} from '../../../ui/sharedContexts/UserSessionContext';
import {State} from '../../../utils/hooks/useAsyncOperation';
import {useMemoizedFunction} from '../../../utils/hooks/useMemoizedFunction';
import {Log} from '../../../utils/logger/log';
import {useDropsSubscriptionsContext} from '../context/SubscriptionsProvider';
import {DropsSubscriptionSchema} from '../types';
import {filterOnlySubscribedDropTypes} from '../utils/helpers';

export const useUpdateAllStoredSubscriptions = ({
  state,
  preferredEmail,
}: {
  state: State<UpdateEmailSubscriptionsResponse, Error>;
  preferredEmail: string;
}) => {
  const {email: userEmail} = useUserSessionContext();
  const {ayUser, guest, modalData, setSubscriptions} =
    useDropsSubscriptionsContext();
  const setSubscriptionsMemoized = useMemoizedFunction(setSubscriptions);

  const updateCallback = useCallback(() => {
    if (state.state === 'success') {
      const subscriptions = state.result.subscriptions?.subscriptions;
      const subscribedTypes = filterOnlySubscribedDropTypes(subscriptions);
      const individualDrops = userEmail
        ? {...ayUser?.individualDrops}
        : {...guest?.individualDrops};
      const customEmails = userEmail ? ayUser?.customEmails || {} : {};
      const isSubscribedWithEmailDifferentThanLoggedIn =
        userEmail && preferredEmail !== userEmail;

      if (isSubscribedWithEmailDifferentThanLoggedIn) {
        if (!customEmails[preferredEmail]) {
          customEmails[preferredEmail] = ['all'];
        } else {
          const subscribedIds = customEmails[preferredEmail];

          if (!subscribedIds.includes('all')) {
            customEmails[preferredEmail].push('all');
          }
        }
      }

      const subscription = {} as DropsSubscriptionSchema;

      if (userEmail) {
        if (!modalData.allDropsSubscriptionStatus) {
          const allSubscribedEmails = Object.keys(customEmails).filter(key =>
            customEmails[key].includes('all'),
          );
          allSubscribedEmails.forEach(subscribedEmail => {
            customEmails[subscribedEmail].splice(
              customEmails[subscribedEmail].indexOf('all'),
              1,
            );
          });
        }

        subscription.ayUser = {
          allDrops: {
            isSubscribed: !!modalData.allDropsSubscriptionStatus,
            subscribedTypes,
          },
          customEmails,
          individualDrops,
          email: isSubscribedWithEmailDifferentThanLoggedIn
            ? userEmail
            : preferredEmail,
        };
      } else {
        subscription.guest = {
          allDrops: {
            isSubscribed: !!modalData.allDropsSubscriptionStatus,
            subscribedTypes,
          },
          individualDrops,
          email: preferredEmail,
        };
      }

      setSubscriptionsMemoized.current(subscription);
    }

    if (state.state === 'error') {
      Log.error(state.error.message, {
        error: new Error(state.error.name),
        statusCode: state.error.grpcErrorCode,
      });
    }
  }, [
    state.state,
    modalData.allDropsSubscriptionStatus,
    userEmail,
    preferredEmail,
    ayUser,
    guest,
    setSubscriptionsMemoized,
  ]);

  useEffect(() => {
    updateCallback();
  }, [state.state]);
};
